// v2 lynx — a Eurasian lynx with the full kit: long legs ending in broad
// snowshoe paws, a compact body standing rump-high with a scatter of coat spots,
// a round face framed by the two-lobed white cheek ruff, a white muzzle and
// amber eyes, triangular ears tipped with the long black tufts, and the stubby
// black-tipped bob tail. Two coats: v0 grey-buff winter, v1 russet spotted
// summer. Ported 1:1 from age-of-mcp's v2/lynx.ts, using the library's shared
// `wetland` (eyes, h01, quadLegs) helpers.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const COATS = [
  { body: "#b4a584", rump: "#a89878", chest: "#c2b290", head: "#c8b894",
    leg: "#a08f6e", ruff: "#e8e0d0", paw: "#d8d0c0", spot: "#8a7a5c" },
  { body: "#bc9468", rump: "#ae885e", chest: "#cca47a", head: "#d2aa7e",
    leg: "#a87f54", ruff: "#ecdcc4", paw: "#dcc8a8", spot: "#7e5c3a" },
];

function lynxParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = wetland.quadLegs(0.052, 0.027, 0.095, 0.0075, c.leg,
    { paw: 0.0072, pawColor: c.paw });
  parts.push(
    // compact body standing rump-high
    { geometry: new THREE.IcosahedronGeometry(0.04, 0), color: c.body,
      position: [-0.005, 0.095, 0], scale: [1.6, 0.9, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.03, 0), color: c.rump,
      position: [-0.05, 0.105, 0], scale: [1.0, 0.95, 0.85] },
    { geometry: new THREE.IcosahedronGeometry(0.027, 0), color: c.chest,
      position: [0.045, 0.09, 0], scale: [0.95, 1.0, 0.9] },
    // round face with the two-lobed white cheek ruff
    { geometry: new THREE.IcosahedronGeometry(0.023, 0), color: c.head,
      position: [0.08, 0.118, 0], scale: [1.05, 0.95, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.0125, 0), color: c.ruff,
      position: [0.082, 0.102, 0.014], rotation: [0.4, 0, 0],
      scale: [1.0, 1.1, 0.7] },
    { geometry: new THREE.IcosahedronGeometry(0.0125, 0), color: c.ruff,
      position: [0.082, 0.102, -0.014], rotation: [-0.4, 0, 0],
      scale: [1.0, 1.1, 0.7] },
    // white muzzle and pink-dark nose
    { geometry: new THREE.IcosahedronGeometry(0.0095, 0), color: c.ruff,
      position: [0.098, 0.112, 0], scale: [1.0, 0.8, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.003, 0), color: "#9a5a52",
      position: [0.106, 0.115, 0] },
    ...wetland.eyes(0.092, 0.124, 0.0115, 0.0032, "#c89030"),
    // stubby bob tail with the black tip
    { geometry: new THREE.CylinderGeometry(0.006, 0.005, 0.022, 4), color: c.body,
      position: [-0.075, 0.106, 0], rotation: [0, 0, Math.PI / 2 + 0.3] },
    { geometry: new THREE.IcosahedronGeometry(0.0065, 0), color: "#1a1a1a",
      position: [-0.086, 0.1, 0] },
  );
  // triangular ears with the long black tufts
  for (const s of [1, -1]) {
    parts.push(
      { geometry: new THREE.ConeGeometry(0.0075, 0.02, 4), color: c.head,
        position: [0.072, 0.139, s * 0.0135], rotation: [s * 0.22, 0, 0.05] },
      { geometry: new THREE.ConeGeometry(0.002, 0.014, 3), color: "#1a1a1a",
        position: [0.0712, 0.152, s * 0.0155], rotation: [s * 0.3, 0, 0.1] },
    );
  }
  // a deterministic scatter of coat spots along the upper flanks
  for (let i = 0; i < 3; i++) {
    const x = -0.035 + 0.028 * i + 0.006 * (wetland.h01(v + 3, i) - 0.5);
    const y = 0.112 + 0.006 * (wetland.h01(v + 7, i) - 0.5);
    for (const s of [1, -1]) {
      parts.push({ geometry: new THREE.IcosahedronGeometry(0.0045, 0),
        color: c.spot, position: [x, y, s * 0.0255], scale: [1.1, 0.5, 1.0] });
    }
  }
  return parts;
}

export const lynxV2 = defineModel({
  id: "lynx-v2",
  batchId: "fable",
  name: "Lynx (v2)",
  tags: ["creature","lynx","low-poly","age-of-mcp","fable"],
  summary: "Low-poly lynx.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.12,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => lynxParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => lynxParts(1) },
  ],
  animations: [],
});
